import type { Bed } from './beds'
import type { Room, Wall } from './model'

// Planning dimensions only. Mattress sizes stay fixed as the central stair widens.
export const BRIDGE = {
  name: 'Perpendicular bridge · Twin / Full',
  upperLength: 82,
  upperDepth: 44,
  lowerWidth: 60,
  lowerLength: 82,
  minStairWidth: 30,
  stairRun: 71.25,
  deckHeight: 60,
  height: 90,
  risers: 8,
} as const
export const BRIDGE_MIN_WIDTH = 2 * BRIDGE.upperLength + BRIDGE.minStairWidth
export const BRIDGE_DEPTH = BRIDGE.upperDepth + BRIDGE.stairRun
// Owner's compact paired-stringer concept: shallower landing, flight within the lower-bed length.
export const COMPACT_BRIDGE = { landingDepth: 21, stairRun: 61, depth: 82 } as const
export const isBridge = (bed: Pick<Bed, 'kind'>) => bed.kind === 'bridge'
export const hasBridge = (beds: Bed[]) => beds.some(isBridge)
export const bridgeWall = (rotation: Bed['rotation']): Wall =>
  ({ 0: 'A', 90: 'D', 180: 'C', 270: 'B' })[rotation] as Wall
export const bridgeStairWidth = (bed: Pick<Bed, 'width'>) => bed.width - 2 * BRIDGE.upperLength

/** Keep the whole assembly against its supporting wall. Never scale mattresses. */
export function fitBridgeToRoom(room: Room, bed: Bed): Bed {
  const vertical = bed.rotation % 180 === 90
  const width = Math.max(BRIDGE_MIN_WIDTH, vertical ? room.depth : room.width)
  return {
    ...bed,
    width,
    depth: BRIDGE_DEPTH,
    height: BRIDGE.height,
    x: bed.rotation === 270 ? Math.max(0, room.width - BRIDGE_DEPTH) : 0,
    y: bed.rotation === 180 ? Math.max(0, room.depth - BRIDGE_DEPTH) : 0,
  }
}

export function createBridge(room: Room, wall: Wall, id: string): Bed {
  return fitBridgeToRoom(room, {
    id,
    kind: 'bridge',
    name: BRIDGE.name,
    x: 0,
    y: 0,
    width: BRIDGE_MIN_WIDTH,
    depth: BRIDGE_DEPTH,
    height: BRIDGE.height,
    rotation: ({ A: 0, B: 270, C: 180, D: 90 } as const)[wall],
  })
}

export function bridgeFitsWall(room: Room, wall: Wall): boolean {
  const horizontal = wall === 'A' || wall === 'C'
  return (
    (horizontal ? room.width : room.depth) >= BRIDGE_MIN_WIDTH &&
    (horizontal ? room.depth : room.width) >= BRIDGE_DEPTH + 36 &&
    room.height - BRIDGE.deckHeight - 6 >= 30
  )
}

export function bridgeRects(width: number) {
  const stairWidth = width - 2 * BRIDGE.upperLength
  const lowerY = 0
  return {
    uppers: [
      { x: 0, y: 0, w: BRIDGE.upperLength, h: BRIDGE.upperDepth },
      { x: width - BRIDGE.upperLength, y: 0, w: BRIDGE.upperLength, h: BRIDGE.upperDepth },
    ],
    lowers: [
      {
        x: BRIDGE.upperLength - BRIDGE.lowerWidth,
        y: lowerY,
        w: BRIDGE.lowerWidth,
        h: BRIDGE.lowerLength,
      },
      { x: width - BRIDGE.upperLength, y: lowerY, w: BRIDGE.lowerWidth, h: BRIDGE.lowerLength },
    ],
    landing: { x: BRIDGE.upperLength, y: 0, w: stairWidth, h: BRIDGE.upperDepth },
    stairs: { x: BRIDGE.upperLength, y: BRIDGE.upperDepth, w: stairWidth, h: BRIDGE.stairRun },
    // Both foot-end entries open sideways from the shared landing. Pillows are outermost.
    entries: [
      { x: BRIDGE.upperLength, y: 3, h: 24 },
      { x: width - BRIDGE.upperLength, y: 3, h: 24 },
    ],
  }
}
